import React, { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import PropTypes from 'prop-types'
import { ComboSelector } from 'modules/Forms/MediaPlanner/subcomponents/FormField/ComboSelector'
import { MonoSelector } from 'modules/Forms/MediaPlanner/subcomponents/FormField/MonoSelector'

export const FormField = ({
  type,
  name,
  sub_field,
  multiple,
  options,
  autofill,
  change,
  formValues,
  formErrors,
  dependencies,
  apiDependencies,
  apiMethod,
  getData,
  loading,
  loaded,
  onChange,
  benchmark,
}) => {
  const { t } = useTranslation()

  const isFieldReady = useMemo(() => {
    if (!dependencies?.length) return true

    const allDependenciesFilled = dependencies.every(
      (dependence) => formValues?.[dependence]?.length > 0,
    )
    const hasNoErrors = !dependencies.some(
      (dependence) => formErrors?.[dependence],
    )

    return allDependenciesFilled && hasNoErrors
  }, [dependencies, formValues, formErrors])

  useEffect(() => {
    if (!loading && !loaded && !options.length && isFieldReady && apiMethod) {
      getData({
        benchmark,
        method: apiMethod,
        type: name,
        ...apiDependencies?.reduce(
          (acc, dependency) => ({
            ...acc,
            [dependency]: formValues[dependency],
          }),
          {},
        ),
      })
    }
  }, [loading, loaded, options, isFieldReady, benchmark, apiMethod])

  return type !== 'selector' ? null : sub_field ? (
    <ComboSelector
      label={t(`media_planner::form.${name}.label`)}
      autofill={autofill}
      change={change}
      formValues={formValues}
      onChange={onChange}
      formErrors={formErrors}
      sub_field={sub_field}
      options={options}
      name={name}
    />
  ) : (
    <MonoSelector
      disabled={loading || (!isFieldReady && options.length === 0)}
      label={t(`media_planner::form.${name}.label`)}
      options={options}
      loading={loading}
      name={name}
      multiple={multiple}
      onChange={onChange}
    />
  )
}

FormField.propTypes = {
  type: PropTypes.string,
  name: PropTypes.string,
  sub_field: PropTypes.shape({
    name: PropTypes.string,
    type: PropTypes.string,
    value: PropTypes.string,
  }),
  multiple: PropTypes.bool,
  options: PropTypes.array,
  autofill: PropTypes.func,
  change: PropTypes.func,
  formValues: PropTypes.object,
  formErrors: PropTypes.object,
  dependencies: PropTypes.array,
  apiDependencies: PropTypes.array,
  apiMethod: PropTypes.string,
  getData: PropTypes.func,
  loading: PropTypes.bool,
  onChange: PropTypes.func,
  benchmark: PropTypes.string,
  loaded: PropTypes.bool,
}
